"use client";

import Link from "next/link";
import { Heart, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { isTutor } from "@/lib/role-utils";

const DESTINATIONS = [
  { label: "Feed", href: "/feed" },
  { label: "Forums", href: "/forums" },
  { label: "Study Groups", href: "/study-groups" },
] as const;

export function SocialNudge({
  user,
  onDismiss,
}: {
  user: unknown;
  onDismiss: () => void;
}) {
  const hideStudyGroups = isTutor(user);
  const links = hideStudyGroups
    ? DESTINATIONS.filter((d) => d.href !== "/study-groups")
    : DESTINATIONS;

  const dismiss = () => {
    fetch("/api/user/preferences", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ socialNudgeDismissed: true }),
    }).catch(() => {});
    onDismiss();
  };

  return (
    <div
      role="complementary"
      aria-label="Let's get social"
      className="fixed z-40 right-4 bottom-6 w-[min(22rem,calc(100vw-2rem))] bg-white border border-gray-200 rounded-2xl shadow-lg p-4"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-3 top-3 rounded p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="flex-shrink-0 h-9 w-9 rounded-full bg-blue-50 flex items-center justify-center">
          <Heart className="h-4 w-4 text-blue-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Let’s get social…</p>
          <p className="text-sm text-gray-600 mt-1 leading-snug">
            The best study breakthroughs happen with other people. Jump into the Feed, ask in Forums
            {hideStudyGroups ? "." : ", or say hi in a Study Group."}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {links.map((link) => (
          <Link key={link.href} href={link.href} onClick={dismiss}>
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-3 rounded-full border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
            >
              {link.label}
            </Button>
          </Link>
        ))}
      </div>
    </div>
  );
}
